import * as Sentry from '@sentry/vue'
import { watch, type App } from 'vue'
import type { Router } from 'vue-router'
import { isSentryEnabled, sentryConfig } from '@/config/sentry'
import type { Member } from '@/data/seedMember'
import { useAuth } from '@/composables/useAuth'

function setMemberContext(isAuthenticated: boolean, member: Member | null): void {
  Sentry.setTag('member_authenticated', String(isAuthenticated))
  Sentry.setTag('member_profile_loaded', String(Boolean(member?.id)))

  if (!isAuthenticated || !member?.id) {
    Sentry.setUser(null)
    return
  }

  Sentry.setUser({
    id: member.id,
    email: member.email || undefined,
  })
}

export function initializeSentry(app: App, router: Router): boolean {
  if (typeof window === 'undefined' || !isSentryEnabled || !sentryConfig.dsn) {
    return false
  }

  const hasTracing = sentryConfig.tracesSampleRate > 0
  const hasReplay =
    sentryConfig.replaysSessionSampleRate > 0 || sentryConfig.replaysOnErrorSampleRate > 0

  Sentry.init({
    app,
    dsn: sentryConfig.dsn,
    environment: sentryConfig.environment,
    release: sentryConfig.release,
    attachProps: false,
    integrations: [
      ...(hasTracing
        ? [
            Sentry.browserTracingIntegration({
              router,
              routeLabel: 'name',
            }),
          ]
        : []),
      ...(hasReplay
        ? [
            Sentry.replayIntegration({
              blockAllMedia: true,
              maskAllInputs: true,
              maskAllText: true,
            }),
          ]
        : []),
    ],
    ...(hasTracing
      ? {
          tracesSampleRate: sentryConfig.tracesSampleRate,
          tracePropagationTargets: sentryConfig.tracePropagationTargets,
        }
      : {}),
    ...(hasReplay
      ? {
          replaysSessionSampleRate: sentryConfig.replaysSessionSampleRate,
          replaysOnErrorSampleRate: sentryConfig.replaysOnErrorSampleRate,
        }
      : {}),
  })

  Sentry.setTag('app_area', 'mysmerra_front')

  const { isAuthenticated, member } = useAuth()

  watch(
    () => ({
      isAuthenticated: isAuthenticated.value,
      member: member.value,
    }),
    ({ isAuthenticated, member }) => setMemberContext(isAuthenticated, member),
    { immediate: true },
  )

  return true
}
